import { useEffect, useRef, useState } from "react";

/**
 * App auto-update: watches the service worker for a new version and surfaces
 * a user-approved reload button.
 *
 * Flow:
 *   1. Register /sw.js
 *   2. Check `reg.update()` on load and every 30 minutes
 *   3. When a new worker reaches state "installed" AND there is already an
 *      active controller (= it's an update, not a first install), expose
 *      `updateReady = true` and store the waiting worker.
 *   4. The UI calls `applyUpdate()` which messages the waiting SW to skip
 *      waiting and reloads the page once the new controller takes over.
 */
export default function useAppUpdate() {
  const [updateReady, setUpdateReady] = useState(false);
  const waitingRef = useRef(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let reg;
    let intervalId;
    let onControllerChange;

    const watchWorker = (worker) => {
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          waitingRef.current = worker;
          setUpdateReady(true);
        }
      });
    };

    (async () => {
      try {
        reg = await navigator.serviceWorker.register("/sw.js");

        // Already waiting from a previous visit?
        if (reg.waiting && navigator.serviceWorker.controller) {
          waitingRef.current = reg.waiting;
          setUpdateReady(true);
        }

        // Future updates
        reg.addEventListener("updatefound", () => watchWorker(reg.installing));

        // Reload the page once the new worker takes control
        let reloaded = false;
        onControllerChange = () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        };
        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        // Poll for updates while the tab is open
        const check = () => { try { reg.update(); } catch { /* ignore */ } };
        check();
        intervalId = setInterval(check, 30 * 60 * 1000); // every 30 min
        const onVis = () => { if (!document.hidden) check(); };
        document.addEventListener("visibilitychange", onVis);
      } catch { /* ignore */ }
    })();

    return () => {
      clearInterval(intervalId);
      if (onControllerChange) {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      }
    };
  }, []);

  const applyUpdate = () => {
    const waiting = waitingRef.current;
    if (!waiting) {
      // Fallback: hard reload
      window.location.reload();
      return;
    }
    waiting.postMessage({ type: "SKIP_WAITING" });
    // The controllerchange listener will reload once the new SW takes over.
  };

  return { updateReady, applyUpdate };
}
